/**
 * @fileoverview Core business types for Agent Console features.
 * Types and constants are derived from shared_constants.json (the source of truth).
 * @owner @Appboy/intelligence
 */

import sharedConstants from "@lib/shared_constants";

const AI_SETTINGS = sharedConstants.AI_SETTINGS;

/**
 * Interaction site where an AI agent can be used.
 * - 0 = Canvas: Agent is used in Canvas workflows
 * - 1 = Catalog: Agent is used in Catalog contexts
 */
export type InteractionSite = (typeof INTERACTION_SITES)[keyof typeof INTERACTION_SITES];

/** Constants for interaction sites. Derived from shared_constants.json. */
export const INTERACTION_SITES = AI_SETTINGS.INTERACTION_SITES;

/**
 * QA agent interaction sites (Campaign QA and Canvas QA). QA agents are
 * configured with rulesets instead of a prompt and skip the Output wizard step.
 */
const qaInteractionSites = new Set<InteractionSite>([
  INTERACTION_SITES.CAMPAIGN_SETUP,
  INTERACTION_SITES.CANVAS_QA,
]);

/** Returns true when the interaction site is a QA agent type (Campaign QA or Canvas QA). */
export const isQaInteractionSite = (site: InteractionSite | undefined): boolean => {
  return site !== undefined && qaInteractionSites.has(site);
};

const interactionSiteValues = new Set<number>(Object.values(INTERACTION_SITES));

/** Type guard validating an untrusted value (e.g. a URL param) as a known interaction site. */
export const isInteractionSite = (value: number): value is InteractionSite => {
  return interactionSiteValues.has(value);
};

/**
 * Thinking/reasoning level for AI model responses.
 * - "none": No extended thinking (lowest cost)
 * - "low": Low thinking effort
 * - "medium": Medium thinking effort
 * - "high": High thinking effort (highest cost, most thorough)
 */
export type ThinkingLevel = (typeof THINKING_LEVELS)[keyof typeof THINKING_LEVELS];

/** Constants for thinking levels. Derived from shared_constants.json. */
export const THINKING_LEVELS = AI_SETTINGS.THINKING_LEVELS;

/**
 * Braze-added context type appended to an agent prompt at execution time. The content is
 * never shown to customers; invocation logs surface only these type names.
 *
 * Sourced from `Braze::Agentic::BrazeContextTypes::Keys` via shared_constants.json, so adding
 * a type in Ruby makes any exhaustive mapping over this union fail to compile until it is
 * handled on the frontend.
 */
export type BrazeContextType = (typeof BRAZE_CONTEXT_TYPES)[keyof typeof BRAZE_CONTEXT_TYPES];

/** Constants for Braze-added context types. Derived from shared_constants.json. */
export const BRAZE_CONTEXT_TYPES = AI_SETTINGS.BRAZE_CONTEXT_TYPES;

const BRAZE_CONTEXT_TYPE_VALUES = new Set<string>(Object.values(BRAZE_CONTEXT_TYPES));

/**
 * Type guard: returns true when `type` is a Braze-added context type known to this build.
 * Invocation logs are stored in Mongo, so a log written by a newer backend can carry a key
 * this build cannot label — narrow with this rather than assuming the union is exhaustive.
 */
export const isBrazeContextType = (type: string | undefined): type is BrazeContextType => {
  return type !== undefined && BRAZE_CONTEXT_TYPE_VALUES.has(type);
};

/**
 * Preset ruleset type identifier.
 * Preset rulesets have an immutable title derived from the type, and may carry
 * type-specific behaviors (e.g. attaching liquid instructions for
 * "personalization_and_dynamic_content").
 *
 * Sourced from `Braze::Agentic::Ruleset::PresetTypes` via shared_constants.json.
 */
export type PresetRulesetType = (typeof PRESET_RULESET_TYPES)[keyof typeof PRESET_RULESET_TYPES];

/** Constants for preset ruleset types. Derived from shared_constants.json. */
export const PRESET_RULESET_TYPES = AI_SETTINGS.QA_AGENTS.PRESET_RULESET_TYPES;

const PRESET_RULESET_TYPE_VALUES = new Set<string>(Object.values(PRESET_RULESET_TYPES));

/** Type guard: returns true when `type` is a known preset ruleset type. */
export const isPresetRulesetType = (type: string | undefined): type is PresetRulesetType => {
  return type !== undefined && PRESET_RULESET_TYPE_VALUES.has(type);
};

/**
 * Ruleset scope identifier. Distinguishes which Canvas QA tab a ruleset belongs
 * to: `canvas` rulesets are evaluated once for the Canvas (Canvas Configuration
 * tab), while the step scopes are evaluated per canvas step of their type under
 * the Canvas steps tab (`message_step` per message step, `audience_paths_step`
 * per Audience Paths step). Campaign QA rulesets leave scope undefined.
 *
 * Sourced from `Braze::Agentic::Ruleset::Scopes` via shared_constants.json.
 */
export type RulesetScope = (typeof RULESET_SCOPES)[keyof typeof RULESET_SCOPES];

/** Constants for ruleset scopes. Derived from shared_constants.json. */
export const RULESET_SCOPES = AI_SETTINGS.QA_AGENTS.RULESET_SCOPES;

/**
 * The ruleset scopes evaluated per canvas step (as opposed to once per
 * Canvas): `message_step` per message step, `audience_paths_step` per
 * Audience Paths step. All step scopes run together as the single "Canvas
 * steps" run: one request, one run id, one panel section. The frontend mirror
 * of `Braze::Agentic::Ruleset::Scopes.step_scopes`; see `isStepRulesetScope`.
 */
export type StepRulesetScope = typeof RULESET_SCOPES.MESSAGE_STEP | typeof RULESET_SCOPES.AUDIENCE_PATHS_STEP;
